import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { generateId } from '@ibe/utils';

import {
  rateMemberConditionalPrice,
  type CatalogueRate,
} from '../../catalogue/api/catalogue-api.js';

export interface SelectedRoom {
  readonly roomTypeId: string;
  readonly ratePlanId: string;
  readonly rooms: number;
  readonly label: string;
  /**
   * Raw source-language display parts for locale-aware rendering. `label`
   * remains for older session state and non-display consumers; these let the
   * UI translate room/rate names while the official property name stays
   * untouched.
   */
  readonly propertyName?: string | undefined;
  readonly roomName?: string | undefined;
  readonly ratePlanName?: string | undefined;
  readonly pricePerNight: number;
  /** Room type's cover image, for the checkout room cards. */
  readonly imageUrl?: string | undefined;
  /** Per-room note collected on the checkout page's room card. */
  readonly specialRequests?: string | undefined;
  /**
   * The durable server-side quote this selection was priced from. The
   * reservation API consumes this rather than trusting client-computed
   * totals, so the frontend never becomes the financial authority.
   */
  readonly quoteId?: string | undefined;
  readonly quote?: CatalogueRate | undefined;
}

/** Charged total for one selected room, from its frozen server-side quote. */
export function selectedRoomTotal(room: SelectedRoom): number {
  const quote = room.quote;
  if (quote === undefined) return room.pricePerNight;
  // grandTotal is the amount due — room total less discounts plus taxes and
  // fees. `price` alone is the pre-tax room total and would under-charge.
  return Number(quote.grandTotal);
}

/**
 * Accommodation-only total shown as the room card's sub total — the room
 * charge before taxes and fees, matching the legacy summary line.
 */
export function selectedRoomSubTotal(room: SelectedRoom): number {
  const quote = room.quote;
  if (quote === undefined) return room.pricePerNight;
  return Number(quote.discountedRoomTotal);
}

/**
 * The member-priced total advertised for a selected room, or null when no
 * member promotion applied — the checkout's "join and save" teaser amount.
 * Anonymous offers carry it in `conditionalPrices[MEMBER]`; a signed-in
 * member's quote is already priced at the member rate.
 */
export function selectedRoomMemberTotal(room: SelectedRoom): number | null {
  const quote = room.quote;
  if (quote === undefined) return null;
  const teaser = rateMemberConditionalPrice(quote);
  if (teaser !== undefined && Number(teaser.grandTotal) < Number(quote.grandTotal)) {
    return Number(teaser.grandTotal);
  }
  return Number(quote.discounts) > 0 ? Number(quote.grandTotal) : null;
}

/**
 * Merge a fresh pick into the existing selection for multi-room bookings.
 * The checkout shows one card per room type: re-picking an already selected
 * room type swaps its rate plan in place — the Edit/back path is how a guest
 * changes rate — while a different room type appends a new line. The card's
 * special-request note survives the swap, and the search's `rooms` count
 * already covers booking several of one type.
 */
export function upsertSelectedRoom(
  rooms: readonly SelectedRoom[],
  next: SelectedRoom,
): readonly SelectedRoom[] {
  const index = rooms.findIndex((room) => room.roomTypeId === next.roomTypeId);
  if (index < 0) return [...rooms, next];
  return rooms.map((room, i) =>
    i === index ? { ...next, specialRequests: room.specialRequests } : room,
  );
}

/**
 * Swap the pick under one room type for a new one — the checkout card's
 * Edit path. A different room type replaces the edited card in place rather
 * than appending a second line, and the card's special-request note
 * survives the swap the same way it does on a rate change. If the new pick
 * is a room type already on another card the two merge — there is one card
 * per room type. A `roomTypeId` that is not on the booking falls back to
 * the plain upsert.
 */
export function replaceSelectedRoom(
  rooms: readonly SelectedRoom[],
  roomTypeId: string,
  next: SelectedRoom,
): readonly SelectedRoom[] {
  const index = rooms.findIndex((room) => room.roomTypeId === roomTypeId);
  const target = index < 0 ? undefined : rooms[index];
  if (target === undefined || roomTypeId === next.roomTypeId) {
    return upsertSelectedRoom(rooms, next);
  }
  const carried: SelectedRoom = { ...next, specialRequests: target.specialRequests };
  const remaining = rooms.filter((_, i) => i !== index);
  if (remaining.some((room) => room.roomTypeId === next.roomTypeId)) {
    return upsertSelectedRoom(remaining, carried);
  }
  return [...remaining.slice(0, index), carried, ...remaining.slice(index)];
}

export interface GuestDetails {
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
  readonly phone: string;
  readonly addressLine1: string;
  readonly city: string;
  readonly country: string;
  readonly postalCode: string;
  readonly suburb?: string;
  readonly state?: string;
  readonly specialRequests: string;
}

export interface BookingState {
  propertyId: string | null;
  propertyName: string;
  /** One-line property address, for the site header on property pages. */
  propertyAddress: string;
  /** Star rating from the availability detail — legacy shows it beside the name. */
  propertyStarRating: number | null;
  /** Property-level stay times from the availability detail (e.g. "14:00"). */
  checkInTime: string;
  checkOutTime: string;
  /** Hero image shown in the booking summary, from the availability detail. */
  propertyImageUrl: string;
  checkIn: string;
  checkOut: string;
  adults: number;
  children: number;
  rooms: number;
  selectedRooms: readonly SelectedRoom[];
  guestDetails: GuestDetails | null;
  bookingReference: string | null;
  /** Guest access token after register/login. */
  accessToken: string | null;
  /** Guest ID from the auth response. */
  guestId: string | null;
  /** Reservation ID after pencil booking creation. */
  reservationId: string | null;
  /** Payment ID after payment initiation. */
  paymentId: string | null;
  /** Gateway type: 1 = Quickstream/TrustedFrame, 2 = Windcave. */
  gatewayType: number | null;
  /** Gateway name: 'windcave' or 'quickstream'. */
  gatewayName: string | null;
  /** Grand total in minor units, for payment confirmation. */
  grandTotal: string | null;
  /** Currency of the quoted total. */
  currency: string;
  /**
   * The property's own currency. Quotes arrive converted to `currency`; when
   * the two differ the checkout shows the legacy property-currency notice.
   */
  propertyCurrency?: string;
  /** Promo code the current prices were quoted under — kept so a mid-checkout
   *  reprice (sign-in/out) asks for the same promotional pricing again. */
  promoCode: string;
  /** Unique per booking attempt; preserved for retries, regenerated for new attempts. */
  idempotencyKey: string | null;
}

const DEFAULT_STATE: BookingState = {
  propertyId: null,
  propertyName: '',
  propertyAddress: '',
  propertyStarRating: null,
  checkInTime: '',
  checkOutTime: '',
  propertyImageUrl: '',
  checkIn: '',
  checkOut: '',
  adults: 2,
  children: 0,
  rooms: 1,
  selectedRooms: [],
  guestDetails: null,
  bookingReference: null,
  accessToken: null,
  guestId: null,
  reservationId: null,
  paymentId: null,
  gatewayType: null,
  gatewayName: null,
  grandTotal: null,
  currency: 'AUD',
  propertyCurrency: 'AUD',
  promoCode: '',
  idempotencyKey: null,
};

const BOOKING_STORAGE_KEY = 'ibe.booking';
// The sign-in pair lives in localStorage, not the session blob — a member's
// session is meant to survive tab closes and browser restarts (the token
// itself carries the expiry), and signing in on one tab signs in on all.
const AUTH_STORAGE_KEY = 'ibe.guest.auth';
const PENDING_STORAGE_PREFIX = 'ibe.pending.';

interface StoredGuestAuth {
  readonly accessToken: string | null;
  readonly guestId: string | null;
}

function loadGuestAuth(): StoredGuestAuth {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (raw === null) return { accessToken: null, guestId: null };
    const parsed = JSON.parse(raw) as Partial<StoredGuestAuth>;
    return {
      accessToken: typeof parsed.accessToken === 'string' ? parsed.accessToken : null,
      guestId: typeof parsed.guestId === 'string' ? parsed.guestId : null,
    };
  } catch {
    return { accessToken: null, guestId: null };
  }
}

function saveGuestAuth(auth: StoredGuestAuth): void {
  try {
    if (auth.accessToken === null && auth.guestId === null) {
      localStorage.removeItem(AUTH_STORAGE_KEY);
    } else {
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(auth));
    }
  } catch {
    // storage may be disabled
  }
}

function loadBookingState(): BookingState {
  try {
    const raw = sessionStorage.getItem(BOOKING_STORAGE_KEY);
    const parsed =
      raw === null ? ({} as Partial<BookingState>) : (JSON.parse(raw) as Partial<BookingState>);
    const auth = loadGuestAuth();
    // localStorage wins; the session blob keeps working for pairs stored
    // before the split so in-flight sessions are not signed out by an upgrade.
    return {
      ...DEFAULT_STATE,
      ...parsed,
      accessToken: auth.accessToken ?? parsed.accessToken ?? null,
      guestId: auth.guestId ?? parsed.guestId ?? null,
    };
  } catch {
    return DEFAULT_STATE;
  }
}

function saveBookingState(state: BookingState): void {
  try {
    const { accessToken: _accessToken, guestId: _guestId, ...booking } = state;
    sessionStorage.setItem(BOOKING_STORAGE_KEY, JSON.stringify(booking));
  } catch {
    // sessionStorage can be unavailable or full; the booking just won't survive a refresh.
  }
  saveGuestAuth({ accessToken: state.accessToken, guestId: state.guestId });
}

export interface BookingContextValue {
  state: BookingState;
  setProperty: (
    id: string,
    name: string,
    details?: {
      checkInTime?: string | undefined;
      checkOutTime?: string | undefined;
      imageUrl?: string | undefined;
      propertyCurrency?: string | undefined;
      address?: string | undefined;
      starRating?: number | undefined;
    },
  ) => void;
  setDates: (checkIn: string, checkOut: string) => void;
  setOccupancy: (adults: number, children: number, rooms: number) => void;
  setSelectedRooms: (rooms: readonly SelectedRoom[]) => void;
  /** Append one room to the selection — the "Add another room" path. */
  addSelectedRoom: (room: SelectedRoom) => void;
  /** Drop one room by index — the checkout card's Remove action. */
  removeSelectedRoom: (index: number) => void;
  /** Store the per-room special-request note typed on a checkout card. */
  setRoomSpecialRequests: (index: number, text: string) => void;
  setGuestDetails: (details: GuestDetails) => void;
  setBookingReference: (ref: string) => void;
  setGuestAuth: (token: string | null, guestId: string | null) => void;
  setReservation: (id: string, bookingReference: string) => void;
  setPayment: (paymentId: string) => void;
  setGateway: (type: number, name: string) => void;
  setGrandTotal: (total: string) => void;
  setCurrency: (currency: string) => void;
  setPromoCode: (code: string) => void;
  setIdempotencyKey: (key: string) => void;
  reset: (options?: ResetOptions) => void;
}

export interface ResetOptions {
  /**
   * Keep `ibe.pending.reservationToken` in sessionStorage. The confirmation
   * page resets the finished booking on success but still needs the token if
   * the guest refreshes the page.
   */
  readonly keepReservationToken?: boolean;
}

const BookingContext = createContext<BookingContextValue | null>(null);

/**
 * The booking a guest is part-way through.
 *
 * **Every setter is stable and the value is memoised**, which is load-bearing
 * rather than an optimisation. Rebuilt on each render, a setter is a new
 * function identity every time — so any consumer that lists one in an effect's
 * dependencies runs that effect on every render, and if the effect sets state
 * the two spin forever. That is not hypothetical: it hung this application's
 * entire test suite, with no failure message, until the setters were made
 * stable.
 *
 * The memoised value also stops every page in the flow re-rendering whenever
 * any part of the booking changes.
 */
export function BookingProvider({ children }: { readonly children: ReactNode }): React.JSX.Element {
  const [state, setState] = useState<BookingState>(loadBookingState);

  useEffect(() => {
    saveBookingState(state);
  }, [state]);

  // The auth pair is shared localStorage state — a sign-in or sign-out on
  // another tab lands here as a `storage` event, and this tab follows.
  useEffect(() => {
    const onStorage = (event: StorageEvent): void => {
      if (event.storageArea !== localStorage || event.key !== AUTH_STORAGE_KEY) return;
      const auth = loadGuestAuth();
      setState((current) => {
        if (current.accessToken === auth.accessToken && current.guestId === auth.guestId) {
          return current;
        }
        return {
          ...current,
          accessToken: auth.accessToken,
          guestId: auth.guestId,
          guestDetails:
            auth.guestId !== null && auth.guestId !== current.guestId ? null : current.guestDetails,
        };
      });
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const setProperty = useCallback(
    (
      id: string,
      name: string,
      details?: {
        checkInTime?: string | undefined;
        checkOutTime?: string | undefined;
        imageUrl?: string | undefined;
        propertyCurrency?: string | undefined;
        address?: string | undefined;
        starRating?: number | undefined;
      },
    ) => {
      setState((current) => ({
        ...current,
        propertyId: id,
        propertyName: name,
        // A quote is priced for one property — browsing another retires any
        // rooms picked under the old one.
        ...(current.propertyId !== null && current.propertyId !== id ? { selectedRooms: [] } : {}),
        ...(details?.checkInTime !== undefined ? { checkInTime: details.checkInTime } : {}),
        ...(details?.checkOutTime !== undefined ? { checkOutTime: details.checkOutTime } : {}),
        ...(details?.imageUrl !== undefined ? { propertyImageUrl: details.imageUrl } : {}),
        ...(details?.address !== undefined ? { propertyAddress: details.address } : {}),
        ...(details?.starRating !== undefined ? { propertyStarRating: details.starRating } : {}),
        ...(details?.propertyCurrency !== undefined
          ? { propertyCurrency: details.propertyCurrency }
          : {}),
      }));
    },
    [],
  );

  const setDates = useCallback((checkIn: string, checkOut: string) => {
    setState((current) => ({
      ...current,
      checkIn,
      checkOut,
      // A quote is priced for one stay — date changes retire earlier picks
      // so a stale quote can never reach checkout or submit.
      ...(current.checkIn !== checkIn || current.checkOut !== checkOut
        ? { selectedRooms: [] }
        : {}),
    }));
  }, []);

  const setOccupancy = useCallback((adults: number, children: number, rooms: number) => {
    setState((current) => ({
      ...current,
      adults,
      children,
      rooms,
      // Occupancy is part of the price — changing it retires earlier picks
      // the same way a date change does.
      ...(current.adults !== adults || current.children !== children || current.rooms !== rooms
        ? { selectedRooms: [], grandTotal: null }
        : {}),
    }));
  }, []);

  const setSelectedRooms = useCallback((rooms: BookingState['selectedRooms']) => {
    setState((current) => ({
      ...current,
      selectedRooms: rooms,
      // A changed item set retires the persisted total — it was priced for
      // the old selection; callers that reprice stamp a fresh grandTotal.
      grandTotal: null,
      idempotencyKey: generateId(),
    }));
  }, []);

  const addSelectedRoom = useCallback((room: SelectedRoom) => {
    // A changed item set is a new booking attempt — the idempotency key must
    // rotate or a resubmit would collide with the reservation already made.
    setState((current) => ({
      ...current,
      selectedRooms: [...current.selectedRooms, room],
      grandTotal: null,
      idempotencyKey: generateId(),
    }));
  }, []);

  const removeSelectedRoom = useCallback((index: number) => {
    setState((current) => ({
      ...current,
      selectedRooms: current.selectedRooms.filter((_, i) => i !== index),
      grandTotal: null,
      idempotencyKey: generateId(),
    }));
  }, []);

  const setRoomSpecialRequests = useCallback((index: number, text: string) => {
    setState((current) => ({
      ...current,
      selectedRooms: current.selectedRooms.map((room, i) =>
        i === index ? { ...room, specialRequests: text } : room,
      ),
    }));
  }, []);

  const setGuestDetails = useCallback((details: BookingState['guestDetails']) => {
    setState((current) => ({ ...current, guestDetails: details }));
  }, []);

  const setBookingReference = useCallback((ref: string) => {
    setState((current) => ({ ...current, bookingReference: ref }));
  }, []);

  const setGuestAuth = useCallback((token: string | null, guestId: string | null) => {
    setState((current) => {
      const nextGuestId = guestId && guestId !== '' ? guestId : null;
      return {
        ...current,
        accessToken: token && token !== '' ? token : null,
        guestId: nextGuestId,
        // Signing in as a different identity must not inherit the previous
        // guest's typed details — they would outrank the new profile's
        // prefill and leak one guest's PII into another's session.
        guestDetails:
          nextGuestId !== null && nextGuestId !== current.guestId ? null : current.guestDetails,
      };
    });
  }, []);

  // The reservation response's guestId is the *resolved* staying-guest
  // profile, which may legitimately differ from the authenticated account's
  // profile — it must not overwrite the session's guestId or the profile
  // endpoints start answering 403 against a token for someone else.
  const setReservation = useCallback((id: string, ref: string) => {
    setState((current) => ({
      ...current,
      reservationId: id,
      bookingReference: ref,
    }));
  }, []);

  const setPayment = useCallback((paymentId: string) => {
    setState((current) => ({ ...current, paymentId }));
  }, []);

  const setGateway = useCallback((type: number, name: string) => {
    setState((current) => ({ ...current, gatewayType: type, gatewayName: name }));
  }, []);

  const setGrandTotal = useCallback((total: string) => {
    setState((current) => ({ ...current, grandTotal: total }));
  }, []);

  const setCurrency = useCallback((currency: string) => {
    setState((current) => ({ ...current, currency }));
  }, []);

  const setPromoCode = useCallback((code: string) => {
    setState((current) => ({ ...current, promoCode: code }));
  }, []);

  const setIdempotencyKey = useCallback((key: string) => {
    setState((current) => ({ ...current, idempotencyKey: key }));
  }, []);

  const reset = useCallback((options?: ResetOptions) => {
    try {
      sessionStorage.removeItem(BOOKING_STORAGE_KEY);
      // The pending payment keys and the reservation token belong to this
      // booking attempt — leaving them behind would let a finished booking's
      // token leak into the next one. The confirmation page is the exception:
      // it resets on success but keeps the token so a refresh can still read
      // the detail.
      const keepToken = options?.keepReservationToken === true;
      const pendingKeys: string[] = [];
      for (let index = 0; index < sessionStorage.length; index += 1) {
        const key = sessionStorage.key(index);
        if (key?.startsWith(PENDING_STORAGE_PREFIX) !== true) continue;
        if (keepToken && key === 'ibe.pending.reservationToken') continue;
        pendingKeys.push(key);
      }
      for (const key of pendingKeys) sessionStorage.removeItem(key);
    } catch {
      // storage may be disabled
    }
    // The guest's session is independent of the booking attempt — a reset
    // ("Start a new booking", "Back to home", currency change) must not sign
    // them out.
    setState((current) => ({
      ...DEFAULT_STATE,
      accessToken: current.accessToken,
      guestId: current.guestId,
    }));
  }, []);

  const value = useMemo<BookingContextValue>(
    () => ({
      state,
      setProperty,
      setDates,
      setOccupancy,
      setSelectedRooms,
      addSelectedRoom,
      removeSelectedRoom,
      setRoomSpecialRequests,
      setGuestDetails,
      setBookingReference,
      setGuestAuth,
      setReservation,
      setPayment,
      setGateway,
      setGrandTotal,
      setCurrency,
      setPromoCode,
      setIdempotencyKey,
      reset,
    }),
    [
      state,
      setProperty,
      setDates,
      setOccupancy,
      setSelectedRooms,
      addSelectedRoom,
      removeSelectedRoom,
      setRoomSpecialRequests,
      setGuestDetails,
      setBookingReference,
      setGuestAuth,
      setReservation,
      setPayment,
      setGateway,
      setGrandTotal,
      setCurrency,
      setPromoCode,
      setIdempotencyKey,
      reset,
    ],
  );

  return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>;
}

export function useBooking(): BookingContextValue {
  const ctx = useContext(BookingContext);
  if (ctx === null) {
    throw new Error('useBooking must be used inside a <BookingProvider>');
  }
  return ctx;
}
